import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Button,
  Select,
  MenuItem,
  TableRow,
  useTheme,
  TableCell,
  TextField,
  TableBody,
  TableHead,
  FormControl,
  useMediaQuery,
} from "@material-ui/core";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { he } from "date-fns/locale";
import DateFnsUtils from "@date-io/date-fns";
import apiService from "../../../services/apiService";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";

const ExportData = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [historyByAction, setActionHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [timeRange, setTimeRange] = useState("בשבוע האחרון");
  const [actions, setActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState("3");
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [dateError, setDateError] = useState("");

  const today = new Date();
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(today);

  const timeRanges = [
    "בשבוע האחרון",
    "בחודש האחרון",
    "בשנה האחרונה",
    "טווח מותאם",
  ];
  const formatDateParam = (d) => (d ? d.toISOString().slice(0, 10) : "");

  useEffect(() => {
    const fetchActions = async () => {
      try {
        const response = await apiService.get("/departmentActions");
        setActions(response.data);
        const exists = response.data.some((a) => a._id == 3);
        if (!exists && response.data.length > 0) {
          setSelectedAction(response.data[0]._id);
        }
      } catch (err) {
        console.error("שגיאה בטעינת רשימת הפעולות", err);
        setError("שגיאה בטעינת רשימת הפעולות");
      }
    };
    fetchActions();
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        let url = selectedAction
          ? `/departmentsUpdate/history/action/${selectedAction}`
          : `/departmentsUpdate/history/action`;

        if (timeRange === "טווח מותאם" && customStart && customEnd) {
          url += `?start=${formatDateParam(customStart)}&end=${formatDateParam(customEnd)}`;
        }

        const response = await apiService.get(url);
        setActionHistory(response.data);
      } catch (err) {
        console.error("שגיאה בטעינת ההיסטוריה", err);
        setError("שגיאה בטעינת ההיסטוריה");
      }
    };
    fetchHistory();
  }, [selectedAction, timeRange, customStart, customEnd]);

  useEffect(() => {
    let filtered = [...historyByAction];

    if (search) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        (h) =>
          (h.firstName && h.firstName.toLowerCase().includes(lowerSearch)) ||
          (h.lastName && h.lastName.toLowerCase().includes(lowerSearch)) ||
          (h.personId && h.personId.toString().includes(lowerSearch))
      );
    }

    if (timeRange) {
      const now = new Date();
      let startDate = null;
      if (timeRange === "בשבוע האחרון") {
        startDate = new Date();
        startDate.setDate(now.getDate() - 7);
      } else if (timeRange === "בחודש האחרון") {
        startDate = new Date();
        startDate.setMonth(now.getMonth() - 1);
      } else if (timeRange === "בשנה האחרונה") {
        startDate = new Date();
        startDate.setFullYear(now.getFullYear() - 1);
      } else if (timeRange === "טווח מותאם" && customStart && customEnd) {
        startDate = new Date(customStart);
        const endDate = new Date(customEnd);
        filtered = filtered.filter((h) => {
          const d = new Date(h.createdAt);
          return d >= startDate && d <= endDate;
        });
        setFilteredHistory(filtered);
        return;
      }
      if (startDate) {
        filtered = filtered.filter((h) => new Date(h.createdAt) >= startDate);
      }
    }

    setFilteredHistory(filtered);
  }, [historyByAction, search, timeRange, customStart, customEnd]);

  const handleExport = () => {
    if (filteredHistory.length === 0) return;

    const wsData = filteredHistory.map((row) => ({
      "שם פרטי": row.firstName,
      "שם משפחה": row.lastName,
      "ת.ז": row.personId,
      "פעולה": row.actionName,
    }));

    wsData.push({
      "שם פרטי": "סך הכל:",
      "שם משפחה": filteredHistory.length,
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Export");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });

    const today = new Date().toISOString().split("T")[0];
    const actionName = actions.find((a) => a._id == selectedAction)?.actionName || "כל הפעולות";
    const fileName = `export_${actionName}_${today}.xlsx`;

    saveAs(blob, fileName);
  };

  useEffect(() => {
    if (timeRange === "טווח מותאם") {
      if (customStart && customEnd && customEnd < customStart) {
        setDateError("תאריך הסיום לא יכול להיות לפני תאריך ההתחלה");
      } else {
        setDateError("");
      }
    } else {
      setDateError("");
    }
  }, [customStart, customEnd, timeRange]);

  return (
    <div style={{ padding: "2% 5%", display: "flex", flexDirection: "column", gap: 20 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        gap={16}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={16}
          width="100%"
        >
          {historyByAction.length > 0 && (
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="חיפוש"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ style: { textAlign: "right" } }} // חיפוש לימין
            />
          )}
          <FormControl size="small" fullWidth>
            <Select
              variant="outlined"
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
            >
              <MenuItem value="">כל הפעולות</MenuItem>
              {actions.map((action) => (
                <MenuItem key={action._id} value={action._id}>
                  {action.actionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <Select
              variant="outlined"
              value={timeRange}
              onChange={(e) => {
                setTimeRange(e.target.value);
                if (e.target.value !== "טווח מותאם") {
                  setCustomStart(null);
                  setCustomEnd(null);
                }
              }}
            >
              {timeRanges.map((range, index) => (
                <MenuItem key={index} value={range}>
                  {range}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {timeRange === "טווח מותאם" && (
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={he}>
              <Box display="flex" flexDirection="column" gap={16} width="100%">
                <KeyboardDatePicker
                  fullWidth
                  autoOk
                  variant="inline"
                  inputVariant="outlined"
                  label="מתאריך"
                  format="dd/MM/yyyy"
                  value={customStart}
                  error={Boolean(dateError)}
                  helperText={dateError}
                  onChange={(date) => setCustomStart(date)}
                  maxDate={customEnd || undefined}
                  InputLabelProps={{ shrink: true }}
                  KeyboardButtonProps={{ "aria-label": "change date" }}
                />
                <KeyboardDatePicker
                  fullWidth
                  autoOk
                  variant="inline"
                  inputVariant="outlined"
                  label="עד תאריך"
                  format="dd/MM/yyyy"
                  value={customEnd}
                  error={Boolean(dateError)}
                  helperText={dateError}
                  onChange={(date) => setCustomEnd(date)}
                  minDate={customStart || undefined}
                  InputLabelProps={{ shrink: true }}
                  KeyboardButtonProps={{ "aria-label": "change date" }}
                />
              </Box>
            </MuiPickersUtilsProvider>
          )}

          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={
              dateError ||
              (timeRange === "טווח מותאם" && (!customStart || !customEnd))
            }
          >
            ייצוא לאקסל
          </Button>
        </Box>
      </Box>

      <Box
        style={{
          display: "inline-block",
          backgroundColor: "yellow",
          padding: "4px 12px",
          borderRadius: 8,
          width: "fit-content",
          fontWeight: "bold",
        }}
      >
        בסך הכל: {filteredHistory.length}
      </Box>

      {filteredHistory.length > 0 && (
        <div style={{ width: "100%", maxHeight: 420, overflowY: "auto", overflowX: "auto" }}>
          <Table stickyHeader size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>שם פרטי</TableCell>
                <TableCell>שם משפחה</TableCell>
                <TableCell>ת"ז</TableCell>
                <TableCell>פעולה</TableCell>
                <TableCell>תאריך</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHistory.map((row, index) => (
                <TableRow key={index}>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{row.firstName}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{row.lastName}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{row.personId}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{row.actionName}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{new Date(row.createdAt).toLocaleString("he-IL")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ExportData;
